import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  PhoneIcon,
  PhoneXMarkIcon,
  SignalIcon,
  ArrowPathIcon,
  MicrophoneIcon,
  SpeakerWaveIcon,
  StopIcon,
  UserIcon,
  ClockIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '../UI/Card';
import Button from '../UI/Button';
import LoadingSpinner from '../UI/LoadingSpinner';
import toast from 'react-hot-toast';
import { api } from '../../services/api';
import { getSseAccessToken } from '../../utils/sseAuthToken';
import { useAuth } from '../../contexts/AuthContext';

// ─── G.711 µ-law decoder ────────────────────────────────────────────────────
function mulawToFloat32(mulaw: Uint8Array): Float32Array {
  const out = new Float32Array(mulaw.length);
  for (let i = 0; i < mulaw.length; i++) {
    let u = ~mulaw[i] & 0xFF;
    let t = ((u & 0x0F) << 3) + 132;
    t <<= (u & 0x70) >> 4;
    out[i] = ((u & 0x80) ? (132 - t) : (t - 132)) / 32768.0;
  }
  return out;
}

// ─── G.711 A-law decoder (Telnyx may send PCMA on some PSTN legs) ───────────
function alawToFloat32(input: Uint8Array): Float32Array {
  const out = new Float32Array(input.length);
  for (let i = 0; i < input.length; i++) {
    let u = input[i] ^ 0x55;
    const sign = (u & 0x80) ? -1 : 1;
    u &= 0x7f;
    const exponent = (u >> 4) & 0x07;
    const mantissa = u & 0x0f;
    let sample: number;
    if (exponent === 0) {
      sample = (mantissa << 4) + 8;
    } else {
      sample = ((mantissa << 4) + 264) << (exponent - 1);
    }
    out[i] = (sign * sample) / 32768.0;
  }
  return out;
}

// ─── L16 linear PCM (RTP-style: network byte order = big-endian) ──────────
function l16ToFloat32(bytes: Uint8Array, littleEndian: boolean): Float32Array {
  const n = Math.floor(bytes.length / 2);
  const out = new Float32Array(n);
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  for (let i = 0; i < n; i++) {
    const s = dv.getInt16(i * 2, littleEndian);
    out[i] = s / 32768.0;
  }
  return out;
}

// ─── Monitor WebSocket manager ──────────────────────────────────────────────
//
// Why the audio was playing at 0.5× (slow motion):
//   The backend streams with `stream_track: 'both_tracks'`. Telnyx sends
//   two separate media events per 20 ms time-slot — one for the inbound
//   track (lead's voice) and one for the outbound track (agent's voice).
//   The old code dumped BOTH into the same ring buffer sequentially,
//   doubling the sample count for the same time period → 0.5× speed.
//
// Fix: each track gets its own ring buffer. The audio output callback
// reads from both and sums (mixes) them so the admin hears both sides
// of the conversation at normal speed.
//
// Additional quality improvements:
//   • Native browser sample rate with JS linear-interpolation resampling
//   • AudioWorklet (off-main-thread) with ScriptProcessorNode fallback
//   • Soft underrun handling (partial fill, not hard 600 ms re-buffer)

class MonitorAudio {
  private ws: WebSocket | null = null;
  private audioCtx: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private scriptNode: ScriptProcessorNode | null = null;

  /** Telnyx `start.media_format.sample_rate` (default until start arrives) */
  private inputRate = 8000;
  /** Telnyx `start.media_format.encoding` */
  private mediaEncoding: 'PCMU' | 'PCMA' | 'L16' | 'default' = 'PCMU';
  private l16LittleEndian = false;
  private resampleRatio = 1;

  // Per-track ring buffers for ScriptProcessor fallback
  // (AudioWorklet keeps its own buffers internally)
  private trackBuffers: Record<string, { ring: Float32Array; w: number; r: number }> = {};
  private prebufferSamples = 0;
  private bufferSize = 0;
  private spBuffering = true;

  // Health check: track frame count and fire callback if no audio
  private frameCount = 0;
  private healthTimer: ReturnType<typeof setTimeout> | null = null;
  onHealthWarning: (() => void) | null = null;

  // ─── Public API ──────────────────────────────────────────────────

  async connect(callControlId: string) {
    this.audioCtx = new AudioContext();
    const outRate = this.audioCtx.sampleRate;
    this.resampleRatio = outRate / this.inputRate;
    this.prebufferSamples = Math.floor(outRate * 0.35); // 350 ms jitter buffer
    this.bufferSize = outRate * 10; // 10 s ring capacity

    if (this.audioCtx.state === 'suspended') {
      await this.audioCtx.resume().catch(() => {});
    }

    let usingWorklet = false;
    try {
      await this.initWorklet();
      usingWorklet = true;
    } catch (e) {
      console.warn('[Monitor] AudioWorklet unavailable, using ScriptProcessorNode fallback', e);
      this.initScriptProcessor();
    }

    console.log(
      `[Monitor] Audio output: ${outRate} Hz, input ${this.inputRate} Hz, resample ratio: ${this.resampleRatio.toFixed(1)}x, ` +
        `renderer: ${usingWorklet ? 'AudioWorklet' : 'ScriptProcessor'}`
    );

    this.initWebSocket(callControlId);

    // Health check: warn if no audio frames arrive within 10 seconds
    this.frameCount = 0;
    this.healthTimer = setTimeout(() => {
      if (this.frameCount === 0) {
        console.warn('[Monitor] No audio frames received after 10s — Telnyx stream may have failed to connect');
        this.onHealthWarning?.();
      } else {
        console.log(`[Monitor] Health OK: ${this.frameCount} frames received in first 10s`);
      }
    }, 10_000);
  }

  disconnect() {
    if (this.healthTimer) { clearTimeout(this.healthTimer); this.healthTimer = null; }
    this.ws?.close();
    this.ws = null;
    this.workletNode?.disconnect();
    this.workletNode = null;
    this.scriptNode?.disconnect();
    this.scriptNode = null;
    this.audioCtx?.close().catch(() => {});
    this.audioCtx = null;
    this.trackBuffers = {};
    this.spBuffering = true;
  }

  // ─── Resampling ──────────────────────────────────────────────────

  private resample(input: Float32Array): Float32Array {
    if (this.resampleRatio === 1) return input;
    const outLen = Math.ceil(input.length * this.resampleRatio);
    const out = new Float32Array(outLen);
    for (let i = 0; i < outLen; i++) {
      const srcPos = i / this.resampleRatio;
      const idx = Math.floor(srcPos);
      const frac = srcPos - idx;
      const s0 = input[idx] ?? 0;
      const s1 = input[Math.min(idx + 1, input.length - 1)] ?? 0;
      out[i] = s0 + frac * (s1 - s0);
    }
    return out;
  }

  private applyTelnyxStart(start: {
    media_format?: { encoding?: string; sample_rate?: number; channels?: number };
  }) {
    const enc = (start.media_format?.encoding || 'PCMU').toUpperCase();
    const sr = start.media_format?.sample_rate;
    if (typeof sr === 'number' && sr > 0) {
      this.inputRate = sr;
    }
    if (enc === 'PCMU' || enc === 'G711U' || enc === 'MULAW') {
      this.mediaEncoding = 'PCMU';
    } else if (enc === 'PCMA' || enc === 'G711A' || enc === 'ALAW') {
      this.mediaEncoding = 'PCMA';
    } else if (enc === 'L16' || enc === 'PCM' || enc === 'LINEAR16') {
      this.mediaEncoding = 'L16';
      // Try BE first (RTP); if audio is wrong, Telnyx may use LE — user can still test PCMU path
      this.l16LittleEndian = false;
    } else if (enc === 'DEFAULT') {
      this.mediaEncoding = 'default';
    } else {
      this.mediaEncoding = 'PCMU';
    }
    if (this.audioCtx) {
      const outRate = this.audioCtx.sampleRate;
      this.resampleRatio = outRate / this.inputRate;
    }
    console.log('[Monitor] Telnyx stream start', {
      encoding: this.mediaEncoding,
      sampleRate: this.inputRate,
      resampleRatio: this.resampleRatio,
    });
  }

  private decodeMediaPayload(bytes: Uint8Array): Float32Array {
    const enc = this.mediaEncoding;
    if (enc === 'PCMA') return alawToFloat32(bytes);
    if (enc === 'L16') return l16ToFloat32(bytes, this.l16LittleEndian);
    // PCMU / default
    return mulawToFloat32(bytes);
  }

  // ─── Feed decoded audio for a specific track ─────────────────────

  private feedAudio(pcm8k: Float32Array, track: string) {
    const resampled = this.resample(pcm8k);

    if (this.workletNode) {
      this.workletNode.port.postMessage(
        { type: 'audio', samples: resampled.buffer, track },
        [resampled.buffer]
      );
    } else {
      // ScriptProcessorNode path — write into per-track ring buffer
      if (!this.trackBuffers[track]) {
        this.trackBuffers[track] = {
          ring: new Float32Array(this.bufferSize),
          w: 0,
          r: 0,
        };
      }
      const tb = this.trackBuffers[track];
      for (let i = 0; i < resampled.length; i++) {
        tb.ring[tb.w] = resampled[i];
        tb.w = (tb.w + 1) % tb.ring.length;
      }
    }
  }

  // ─── AudioWorklet setup (dual-track mixing inside worklet) ───────

  private async initWorklet() {
    const code = `
class MonitorProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    // Per-track ring buffers: { [trackName]: { ring, w, r } }
    this.tracks = {};
    this.bufSize = sampleRate * 10;
    this.buffering = true;
    this.pre = Math.floor(sampleRate * 0.35);

    this.port.onmessage = (e) => {
      if (e.data.type !== 'audio') return;
      const name = e.data.track || 'default';
      if (!this.tracks[name]) {
        this.tracks[name] = {
          ring: new Float32Array(this.bufSize),
          w: 0,
          r: 0,
        };
      }
      const t = this.tracks[name];
      const buf = new Float32Array(e.data.samples);
      for (let i = 0; i < buf.length; i++) {
        t.ring[t.w] = buf[i];
        t.w = (t.w + 1) % t.ring.length;
      }
    };
  }

  process(inputs, outputs) {
    const out = outputs[0][0];
    if (!out) return true;

    const names = Object.keys(this.tracks);
    if (names.length === 0) { out.fill(0); return true; }

    // Find the max available across tracks so one silent/delayed track
    // doesn't block the other from playing.
    let maxAvail = 0;
    for (const n of names) {
      const t = this.tracks[n];
      const a = (t.w - t.r + t.ring.length) % t.ring.length;
      if (a > maxAvail) maxAvail = a;
    }

    if (this.buffering) {
      if (maxAvail < this.pre) { out.fill(0); return true; }
      this.buffering = false;
    }

    const toRead = Math.min(maxAvail, out.length);
    out.fill(0);

    // Mix: sum all tracks, scale by 0.7 to leave headroom
    for (const n of names) {
      const t = this.tracks[n];
      const a = (t.w - t.r + t.ring.length) % t.ring.length;
      const readable = Math.min(a, toRead);
      for (let i = 0; i < readable; i++) {
        out[i] += t.ring[t.r] * 0.7;
        t.r = (t.r + 1) % t.ring.length;
      }
    }

    if (maxAvail === 0) this.buffering = true;
    return true;
  }
}
registerProcessor('monitor-processor', MonitorProcessor);
`;

    const blob = new Blob([code], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    try {
      await this.audioCtx!.audioWorklet.addModule(url);
    } finally {
      URL.revokeObjectURL(url);
    }

    this.workletNode = new AudioWorkletNode(this.audioCtx!, 'monitor-processor');
    this.workletNode.connect(this.audioCtx!.destination);
  }

  // ─── ScriptProcessorNode fallback (dual-track mixing) ────────────

  private initScriptProcessor() {
    this.scriptNode = this.audioCtx!.createScriptProcessor(4096, 1, 1);
    this.scriptNode.onaudioprocess = (e: AudioProcessingEvent) => {
      const output = e.outputBuffer.getChannelData(0);
      const names = Object.keys(this.trackBuffers);

      if (names.length === 0) { output.fill(0); return; }

      // Max available across all tracks
      let maxAvail = 0;
      for (const n of names) {
        const tb = this.trackBuffers[n];
        const a = (tb.w - tb.r + tb.ring.length) % tb.ring.length;
        if (a > maxAvail) maxAvail = a;
      }

      if (this.spBuffering) {
        if (maxAvail < this.prebufferSamples) { output.fill(0); return; }
        this.spBuffering = false;
      }

      const toRead = Math.min(maxAvail, output.length);
      output.fill(0);

      for (const n of names) {
        const tb = this.trackBuffers[n];
        const a = (tb.w - tb.r + tb.ring.length) % tb.ring.length;
        const readable = Math.min(a, toRead);
        for (let i = 0; i < readable; i++) {
          output[i] += tb.ring[tb.r] * 0.7;
          tb.r = (tb.r + 1) % tb.ring.length;
        }
      }

      if (maxAvail === 0) this.spBuffering = true;
    };
    this.scriptNode.connect(this.audioCtx!.destination);
  }

  // ─── WebSocket ───────────────────────────────────────────────────

  private initWebSocket(callControlId: string) {
    const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const backendOrigin = apiBase.replace(/\/api\/?$/, '').replace(/^http/, 'ws');
    const url = `${backendOrigin}/ws/monitor/${encodeURIComponent(callControlId)}`;
    console.log('[Monitor] Connecting to', url);
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      console.log('[Monitor] WebSocket connected');
    };

    this.ws.onmessage = (ev) => {
      try {
        const text = typeof ev.data === 'string' ? ev.data : null;
        if (!text) return;

        const msg = JSON.parse(text);
        if (msg.event === 'start' && msg.start) {
          this.applyTelnyxStart(msg.start);
        } else if (msg.event === 'media' && msg.media?.payload) {
          this.frameCount++;
          const track = msg.media.track || 'default'; // 'inbound' or 'outbound'
          if (this.frameCount === 1) {
            console.log('[Monitor] First audio frame received', { track, payloadLen: msg.media.payload.length });
          }
          const raw = atob(msg.media.payload);
          const bytes = new Uint8Array(raw.length);
          for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
          this.feedAudio(this.decodeMediaPayload(bytes), track);
        } else if (msg.type === 'connected') {
          console.log('[Monitor] Server confirmed connection for', msg.callControlId, 'telnyxStreamActive:', msg.telnyxStreamActive);
        } else if (msg.type === 'stream_failed') {
          console.error('[Monitor] Telnyx stream failed:', msg.reason);
          this.onHealthWarning?.();
        } else {
          // Log unexpected message types for debugging
          console.log('[Monitor] Received non-media message:', msg.event || msg.type || Object.keys(msg).join(','));
        }
      } catch (err) {
        console.warn('[Monitor] Frame parse error:', err);
      }
    };

    this.ws.onclose = (ev) => {
      console.warn('[Monitor] WebSocket closed', ev.code, ev.reason);
    };

    this.ws.onerror = () => {
      console.warn('[Monitor] WebSocket error');
    };
  }
}

/**
 * Vapi monitor.listenUrl — raw PCM (16-bit LE mono, typically 16 kHz).
 * Browser connects directly; URL is short-lived and call-scoped.
 */
class VapiMonitorAudio {
  private ws: WebSocket | null = null;
  private audioCtx: AudioContext | null = null;
  private scriptNode: ScriptProcessorNode | null = null;
  private ring = new Float32Array(48000 * 10);
  private w = 0;
  private r = 0;
  private inputRate = 16000;
  private frameCount = 0;
  private healthTimer: ReturnType<typeof setTimeout> | null = null;
  onHealthWarning: (() => void) | null = null;

  async connect(listenUrl: string, sampleRate = 16000) {
    this.inputRate = sampleRate || 16000;
    this.audioCtx = new AudioContext();
    if (this.audioCtx.state === 'suspended') {
      await this.audioCtx.resume().catch(() => {});
    }

    const outRate = this.audioCtx.sampleRate;
    const ratio = outRate / this.inputRate;
    this.scriptNode = this.audioCtx.createScriptProcessor(4096, 0, 1);
    this.scriptNode.onaudioprocess = (ev) => {
      const out = ev.outputBuffer.getChannelData(0);
      const needed = out.length;
      const available = (this.w - this.r + this.ring.length) % this.ring.length;
      if (available < needed) {
        out.fill(0);
        return;
      }
      for (let i = 0; i < needed; i++) {
        out[i] = this.ring[this.r];
        this.r = (this.r + 1) % this.ring.length;
      }
    };
    this.scriptNode.connect(this.audioCtx.destination);

    this.ws = new WebSocket(listenUrl);
    this.ws.binaryType = 'arraybuffer';
    this.ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') return;
      const bytes = new Uint8Array(ev.data as ArrayBuffer);
      if (bytes.byteLength < 2) return;
      this.frameCount += 1;
      const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
      const n = Math.floor(bytes.byteLength / 2);
      const repeats = Math.max(1, Math.round(ratio));
      for (let i = 0; i < n; i++) {
        const sample = dv.getInt16(i * 2, true) / 32768;
        for (let k = 0; k < repeats; k++) {
          this.ring[this.w] = sample;
          this.w = (this.w + 1) % this.ring.length;
          if (this.w === this.r) this.r = (this.r + 1) % this.ring.length;
        }
      }
    };
    this.ws.onerror = () => console.warn('[VapiMonitor] WebSocket error');

    this.frameCount = 0;
    this.healthTimer = setTimeout(() => {
      if (this.frameCount === 0) this.onHealthWarning?.();
    }, 10_000);
  }

  disconnect() {
    if (this.healthTimer) {
      clearTimeout(this.healthTimer);
      this.healthTimer = null;
    }
    this.ws?.close();
    this.ws = null;
    this.scriptNode?.disconnect();
    this.scriptNode = null;
    this.audioCtx?.close().catch(() => {});
    this.audioCtx = null;
  }
}

/** Per-call monitor instances */
const monitors = new Map<string, MonitorAudio | VapiMonitorAudio>();

// ─── Types ──────────────────────────────────────────────────────────────────

export interface LiveCall {
  callControlId: string;
  callAttemptId?: string;
  campaignId?: string;
  campaignName?: string;
  leadId?: string;
  leadName?: string;
  leadPhone?: string;
  agentId?: string;
  agentName?: string;
  agentCallControlId?: string;
  fromNumber?: string;
  toNumber?: string;
  status: 'initiated' | 'ringing' | 'answered' | 'in-progress' | 'bridged' | 'completed' | 'failed';
  direction: 'outbound' | 'inbound';
  startedAt: string;
  answeredAt?: string;
  amdResult?: string;
  /** True only while a human-AMD verdict wait is armed (HUMAN_AMD_ENABLED + cancel). */
  awaitingAmd?: boolean;
  isListening?: boolean;
  duration?: number;
  conferenceId?: string;
  supervisorCallControlId?: string;
  supervisorRole?: 'listen' | 'whisper' | 'barge';
  dialingType?: 'auto' | 'manual';
  campaignType?: string;
}

type LiveCallEvent =
  | { type: 'call_started'; call: LiveCall }
  | { type: 'call_updated'; call: LiveCall }
  | { type: 'call_ended'; callControlId: string; reason?: string }
  | { type: 'calls_snapshot'; calls: LiveCall[] };

// ─── Helpers ────────────────────────────────────────────────────────────────

const statusColors: Record<string, string> = {
  initiated: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  ringing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  answered: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  'in-progress': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  bridged: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  completed: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

function formatDuration(startedAt: string): string {
  const seconds = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatPhone(phone?: string): string {
  if (!phone) return 'Unknown';
  if (phone.startsWith('+1') && phone.length === 12) {
    return `(${phone.slice(2, 5)}) ${phone.slice(5, 8)}-${phone.slice(8)}`;
  }
  return phone;
}

function isBotLiveCall(call: LiveCall): boolean {
  return call.campaignType === 'bot';
}

function isBotConnected(call: LiveCall): boolean {
  return isBotLiveCall(call) && (call.status === 'in-progress' || call.status === 'bridged');
}

/** Human-readable stage label for a call, so admins can tell at a glance
 *  what the dialer is doing (dialing → ringing → AMD check → transfer → live). */
function stageLabel(call: LiveCall): string {
  if (isBotLiveCall(call)) {
    switch (call.status) {
      case 'initiated':
        return 'Dialing…';
      case 'ringing':
        return 'Ringing…';
      case 'answered':
        return 'Connecting…';
      case 'in-progress':
      case 'bridged':
        return call.agentName ? `Live · ${call.agentName}` : 'Connected';
      case 'completed':
        return 'Ended';
      case 'failed':
        return 'Failed';
      default:
        return call.status;
    }
  }

  switch (call.status) {
    case 'initiated':
      return 'Dialing…';
    case 'ringing':
      return 'Ringing…';
    case 'answered':
    case 'in-progress':
      // Human AMD defaults OFF (HUMAN_AMD_ENABLED). Only show the
      // voicemail-check label when a verdict is actually in flight or landed.
      if (call.amdResult === 'machine') return 'Voicemail';
      if (call.agentName) return `Transferring to ${call.agentName}…`;
      if (call.awaitingAmd && !call.amdResult) return 'Answered · checking voicemail…';
      return 'Answered · connecting agent…';
    case 'bridged':
      return call.agentName ? `Live · ${call.agentName}` : 'Live';
    case 'completed':
      return 'Ended';
    case 'failed':
      return 'Failed';
    default:
      return call.status;
  }
}

// ─── Component ──────────────────────────────────────────────────────────────

interface LiveCallsSectionProps {
  /** Show only auto-dialer calls of human campaigns (used by the Auto Dial Monitor). */
  autoDialerOnly?: boolean;
  /** Show only AI bot campaign calls (used by Bot Monitor). */
  botOnly?: boolean;
  /** Hide the Bot Monitor entry link (when already on Bot Monitor). */
  hideBotMonitorLink?: boolean;
}

const LiveCallsSection: React.FC<LiveCallsSectionProps> = ({
  autoDialerOnly = false,
  botOnly = false,
  hideBotMonitorLink = false,
}) => {
  const { user } = useAuth();
  const canForceHangup = user?.role === 'admin' || user?.role === 'owner';
  const [calls, setCalls] = useState<LiveCall[]>([]);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(true);
  const [whisperModal, setWhisperModal] = useState<string | null>(null);
  const [whisperText, setWhisperText] = useState('');
  const [sendingWhisper, setSendingWhisper] = useState(false);
  const [listeningCalls, setListeningCalls] = useState<Set<string>>(new Set());
  const listeningCallsRef = useRef<Set<string>>(new Set());
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<number | undefined>();
  const reconnectAttemptsRef = useRef(0);
  const closedRef = useRef(false);

  // Live duration ticker
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  // ─── SSE Connection ─────────────────────────────────────────────

  const connectStream = useCallback(() => {
    if (closedRef.current) return;
    if (typeof window === 'undefined' || !('EventSource' in window)) return;

    void (async () => {
      // Close any previous socket before opening a new one (reconnect path).
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = undefined;
      }
      eventSourceRef.current?.close();
      eventSourceRef.current = null;

      const token = await getSseAccessToken();
      if (!token) {
        setConnecting(false);
        scheduleReconnect();
        return;
      }
      if (closedRef.current) return;

      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      const fullUrl = baseUrl.startsWith('http')
        ? `${baseUrl}/live-calls/stream`
        : `${window.location.origin}${baseUrl}/live-calls/stream`;
      const url = new URL(fullUrl);
      url.searchParams.set('token', token);

      const es = new EventSource(url.toString());
      eventSourceRef.current = es;

      es.addEventListener('live-call', (event: MessageEvent) => {
        try {
          const data: LiveCallEvent = JSON.parse(event.data);
          switch (data.type) {
            case 'calls_snapshot':
              setCalls(data.calls);
              break;
            case 'call_started':
              setCalls((prev) => {
                const exists = prev.find((c) => c.callControlId === data.call.callControlId);
                return exists
                  ? prev.map((c) => (c.callControlId === data.call.callControlId ? data.call : c))
                  : [...prev, data.call];
              });
              break;
            case 'call_updated':
              setCalls((prev) => prev.map((c) => (c.callControlId === data.call.callControlId ? data.call : c)));
              break;
            case 'call_ended':
              setCalls((prev) => prev.filter((c) => c.callControlId !== data.callControlId));
              setListeningCalls((prev) => {
                const next = new Set(prev);
                next.delete(data.callControlId);
                return next;
              });
              monitors.get(data.callControlId)?.disconnect();
              monitors.delete(data.callControlId);
              cancelListenRetry(data.callControlId);
              break;
          }
        } catch {
          // ignore
        }
      });

      es.addEventListener('ping', () => {
        // keepalive, no-op
      });

      es.onopen = () => {
        setConnected(true);
        setConnecting(false);
        reconnectAttemptsRef.current = 0;
      };

      es.onerror = () => {
        if (closedRef.current) return;
        if (es.readyState !== EventSource.CLOSED) return;
        es.close();
        if (eventSourceRef.current === es) eventSourceRef.current = null;
        setConnected(false);
        scheduleReconnect();
      };
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const scheduleReconnect = useCallback(() => {
    if (closedRef.current || reconnectTimerRef.current) return;
    reconnectAttemptsRef.current += 1;
    const delay = Math.min(30000, 1000 * 2 ** (reconnectAttemptsRef.current - 1));
    setConnecting(true);
    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = undefined;
      connectStream();
    }, delay);
  }, [connectStream]);

  useEffect(() => {
    listeningCallsRef.current = listeningCalls;
  }, [listeningCalls]);

  useEffect(() => {
    closedRef.current = false;
    connectStream();

    const handleVisibility = () => {
      if (closedRef.current) return;
      if (document.visibilityState === 'visible' && !eventSourceRef.current) {
        connectStream();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      closedRef.current = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      for (const callControlId of listeningCallsRef.current) {
        void api.post(`/live-calls/${encodeURIComponent(callControlId)}/stop-listen`).catch(() => {});
      }
      monitors.forEach((m) => m.disconnect());
      monitors.clear();
      listenRetryTimers.current.forEach((t) => clearTimeout(t));
      listenRetryTimers.current.clear();
      listenRetryCount.current.clear();
    };
  }, [connectStream]);

  // ─── Actions ────────────────────────────────────────────────────

  const listenRetryCount = useRef<Map<string, number>>(new Map());
  const listenRetryTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const cancelListenRetry = useCallback((callControlId: string) => {
    const timer = listenRetryTimers.current.get(callControlId);
    if (timer) {
      clearTimeout(timer);
      listenRetryTimers.current.delete(callControlId);
    }
    listenRetryCount.current.delete(callControlId);
  }, []);

  const handleListen = useCallback(async (callControlId: string) => {
    // Don't retry if call no longer exists
    if (listenRetryCount.current.has(callControlId)) {
      const callStillActive = calls.some((c) => c.callControlId === callControlId);
      if (!callStillActive) {
        cancelListenRetry(callControlId);
        return;
      }
    }

    try {
      const res = await api.post<{
        success?: boolean;
        data?: {
          streamCallControlId?: string;
          provider?: string;
          listenUrl?: string;
          sampleRate?: number;
        };
      }>(`/live-calls/${encodeURIComponent(callControlId)}/listen`);
      setListeningCalls((prev) => new Set(prev).add(callControlId));

      if (res.data?.data?.provider === 'vapi' && res.data.data.listenUrl) {
        const monitor = new VapiMonitorAudio();
        monitor.onHealthWarning = () => {
          monitor.disconnect();
          monitors.delete(callControlId);
          setListeningCalls((prev) => {
            const next = new Set(prev);
            next.delete(callControlId);
            return next;
          });
          api.post(`/live-calls/${encodeURIComponent(callControlId)}/stop-listen`).catch(() => {});
          toast.error('No VAPI audio received — try again', { duration: 4000 });
        };
        await monitor.connect(res.data.data.listenUrl, res.data.data.sampleRate || 16000);
        monitors.set(callControlId, monitor);
        cancelListenRetry(callControlId);
        toast.success('Now listening to VAPI call');
        return;
      }

      const streamCallControlId = res.data?.data?.streamCallControlId ?? callControlId;
      const monitor = new MonitorAudio();
      monitor.onHealthWarning = () => {
        monitor.disconnect();
        monitors.delete(callControlId);
        setListeningCalls((prev) => {
          const next = new Set(prev);
          next.delete(callControlId);
          return next;
        });
        api.post(`/live-calls/${encodeURIComponent(callControlId)}/stop-listen`).catch(() => {});

        const retries = listenRetryCount.current.get(callControlId) || 0;
        if (retries < 2) {
          listenRetryCount.current.set(callControlId, retries + 1);
          toast.error('No audio received — retrying...', { duration: 3000 });
          const timer = setTimeout(() => {
            listenRetryTimers.current.delete(callControlId);
            handleListen(callControlId);
          }, 1500);
          listenRetryTimers.current.set(callControlId, timer);
        } else {
          cancelListenRetry(callControlId);
          toast.error('Listen failed after retries. The call may have ended or Telnyx stream is unavailable.', { duration: 8000 });
        }
      };
      await monitor.connect(streamCallControlId);
      monitors.set(callControlId, monitor);
      cancelListenRetry(callControlId);
      toast.success('Now listening to call');
    } catch (err: any) {
      setListeningCalls((prev) => {
        const next = new Set(prev);
        next.delete(callControlId);
        return next;
      });
      monitors.get(callControlId)?.disconnect();
      monitors.delete(callControlId);
      cancelListenRetry(callControlId);
      toast.error(err?.response?.data?.message || 'Failed to start listening');
    }
  }, [calls, cancelListenRetry]);

  const handleStopListen = async (callControlId: string) => {
    try {
      await api.post(`/live-calls/${callControlId}/stop-listen`);
      setListeningCalls((prev) => {
        const next = new Set(prev);
        next.delete(callControlId);
        return next;
      });

      monitors.get(callControlId)?.disconnect();
      monitors.delete(callControlId);

      toast.success('Stopped listening');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to stop listening');
    }
  };

  const handleSwitchRole = async (callControlId: string, role: 'listen' | 'whisper' | 'barge') => {
    // Reserved for future conference-based supervisor role switching
    toast.error('Role switching requires admin WebRTC (coming soon)');
  };

  const handleWhisper = async () => {
    if (!whisperModal || !whisperText.trim()) return;
    setSendingWhisper(true);
    try {
      await api.post(`/live-calls/${whisperModal}/whisper`, { text: whisperText.trim() });
      toast.success('Whisper sent to agent');
      setWhisperText('');
      setWhisperModal(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to send whisper');
    } finally {
      setSendingWhisper(false);
    }
  };

  const handleHangup = async (callControlId: string) => {
    try {
      await api.post(`/live-calls/${encodeURIComponent(callControlId)}/hangup`);
      toast.success('Call ended');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to end call');
    }
  };

  const handleRefresh = async () => {
    try {
      await api.post('/live-calls/clear-stale');
      // Force an immediate reconnect so the latest snapshot is received.
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      reconnectAttemptsRef.current = 0;
      setConnected(false);
      setConnecting(true);
      connectStream();
      toast.success('Live monitor refreshed');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to refresh live calls');
    }
  };

  // Human Live Calls: show ringing through bridged.
  // Live Bot Calls (botOnly): only connected calls — dialing/ringing stay off this board.
  const activeStatuses = botOnly
    ? new Set(['in-progress', 'bridged'])
    : new Set(['ringing', 'answered', 'in-progress', 'bridged']);
  const activeCalls = calls.filter((c) => {
    if (!activeStatuses.has(c.status)) return false;
    if (botOnly) return c.campaignType === 'bot';
    // Auto Dial Monitor: hide manual dials and bot calls. Calls registered
    // before this field existed (dialingType undefined) stay visible.
    if (autoDialerOnly) {
      return c.dialingType !== 'manual' && c.campaignType !== 'bot';
    }
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <Card className="card-elevated overflow-hidden">
        <div className="px-6 py-5 bg-muted/50 border-b flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <SignalIcon className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">
                {botOnly ? 'Live Bot Calls' : 'Live Calls'}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                {botOnly
                  ? 'Connected AI bot calls only — listen in on live audio'
                  : 'Monitor active calls — listen in or whisper to agents'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!hideBotMonitorLink && !botOnly && (
              <Link
                to="/admin/bot-monitor"
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent transition-colors"
              >
                <CpuChipIcon className="h-3.5 w-3.5 text-primary" />
                Bot Monitor
              </Link>
            )}
            <Button
              variant="outlined"
              size="sm"
              onClick={handleRefresh}
              className="flex items-center gap-1 text-xs"
            >
              <ArrowPathIcon className="h-3.5 w-3.5" />
              Refresh
            </Button>
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                connected
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
              }`}
            >
              <span
                className={`h-2 w-2 rounded-full ${
                  connected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                }`}
              />
              {connected ? 'Connected' : connecting ? 'Connecting...' : 'Disconnected'}
            </span>
            <span className="text-sm text-muted-foreground font-medium">
              {activeCalls.length} active call{activeCalls.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Call Cards */}
        <div className="p-6">
          {connecting && activeCalls.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : activeCalls.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <PhoneIcon className="h-14 w-14 text-muted-foreground/30 mb-3" />
              <h3 className="text-base font-medium text-foreground mb-1">No Active Calls</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                {botOnly
                  ? 'When an AI bot call is connected (answered), it will appear here for listen-in.'
                  : 'When agents answer calls through your campaigns, they will appear here in real-time.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {activeCalls.map((call) => {
                const isListening = listeningCalls.has(call.callControlId);
                const isVapiCall = call.callControlId.startsWith('vapi:');
                const isBridged = call.status === 'bridged';
                const botConnected = isBotConnected(call);
                const showConnectedBanner =
                  (isBridged && !!call.agentName) || (botConnected && !!call.agentName);
                const isLiveHighlight = isBridged || botConnected;
                return (
                  <Card
                    key={call.callControlId}
                    className={`relative overflow-hidden transition-all duration-300 ${
                      isLiveHighlight
                        ? 'ring-2 ring-green-500 shadow-lg shadow-green-500/10'
                        : isListening
                        ? 'ring-2 ring-primary shadow-lg'
                        : 'hover:shadow-md'
                    }`}
                  >
                    {/* Status pulse bar */}
                    <div
                      className={`absolute top-0 left-0 right-0 h-1 ${
                        isLiveHighlight
                          ? 'bg-green-500 animate-pulse'
                          : call.status === 'answered' || call.status === 'in-progress'
                          ? 'bg-green-500'
                          : call.status === 'initiated' || call.status === 'ringing'
                          ? 'bg-yellow-500 animate-pulse'
                          : 'bg-gray-400'
                      }`}
                    />

                    <CardContent className="pt-5 pb-4 px-5 space-y-4">
                      {/* Top row */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="shrink-0 h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground truncate">
                              {call.leadName || 'Unknown Contact'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatPhone(call.toNumber || call.leadPhone)}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            statusColors[call.status] || statusColors.initiated
                          }`}
                        >
                          {isLiveHighlight && (
                            <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                          )}
                          {stageLabel(call)}
                        </span>
                      </div>

                      {/* Agent / AI connected banner */}
                      {showConnectedBanner && call.agentName && (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                          <div className="shrink-0 h-7 w-7 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                            {botConnected ? (
                              <CpuChipIcon className="h-4 w-4 text-green-700 dark:text-green-300" />
                            ) : (
                              <UserIcon className="h-4 w-4 text-green-700 dark:text-green-300" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-green-800 dark:text-green-300 truncate">
                              {call.agentName}
                            </p>
                            <p className="text-[10px] text-green-600 dark:text-green-400">
                              {botConnected ? 'AI connected' : 'Agent connected'}
                            </p>
                          </div>
                          <span className="ml-auto shrink-0 inline-flex items-center gap-1 text-[10px] font-medium text-green-700 dark:text-green-300">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                            Live
                          </span>
                        </div>
                      )}

                      {/* Details grid */}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                        {call.campaignName && (
                          <>
                            <span className="text-muted-foreground">Campaign</span>
                            <span className="text-foreground font-medium truncate">{call.campaignName}</span>
                          </>
                        )}
                        <span className="text-muted-foreground">From</span>
                        <span className="text-foreground font-medium">{formatPhone(call.fromNumber)}</span>
                        <span className="text-muted-foreground">Direction</span>
                        <span className="text-foreground font-medium capitalize">{call.direction}</span>
                        {call.amdResult && !isBotLiveCall(call) && (
                          <>
                            <span className="text-muted-foreground">AMD</span>
                            <span className="text-foreground font-medium capitalize">{call.amdResult}</span>
                          </>
                        )}
                        {call.agentName && !showConnectedBanner && (
                          <>
                            <span className="text-muted-foreground">{isBotLiveCall(call) ? 'AI' : 'Agent'}</span>
                            <span className="text-foreground font-medium truncate">{call.agentName}</span>
                          </>
                        )}
                      </div>

                      {/* Duration + Actions */}
                      <div className="flex items-center justify-between pt-2 border-t border-border/50">
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <ClockIcon className="h-4 w-4" />
                          <span className="font-mono">{formatDuration(call.startedAt)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {isListening ? (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleStopListen(call.callControlId)}
                              className="flex items-center gap-1 text-xs"
                            >
                              <StopIcon className="h-3.5 w-3.5" />
                              Stop
                            </Button>
                          ) : (
                            <Button
                              variant="outlined"
                              size="sm"
                              onClick={() => handleListen(call.callControlId)}
                              disabled={call.status === 'initiated' || call.status === 'ringing'}
                              title={
                                call.status === 'initiated' || call.status === 'ringing'
                                  ? 'Call not connected yet — wait for answer'
                                  : isVapiCall
                                    ? 'Listen to this VAPI call'
                                    : 'Listen to this call'
                              }
                              className="flex items-center gap-1 text-xs"
                            >
                              <SpeakerWaveIcon className="h-3.5 w-3.5" />
                              Listen
                            </Button>
                          )}
                          {!botOnly && !isVapiCall && (
                            <Button
                              variant="outlined"
                              size="sm"
                              onClick={() => {
                                setWhisperModal(call.callControlId);
                                setWhisperText('');
                              }}
                              className="flex items-center gap-1 text-xs"
                            >
                              <MicrophoneIcon className="h-3.5 w-3.5" />
                              Whisper
                            </Button>
                          )}
                          {canForceHangup && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleHangup(call.callControlId)}
                              className="flex items-center gap-1 text-xs"
                            >
                              <PhoneXMarkIcon className="h-3.5 w-3.5" />
                              End
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {/* Whisper Modal */}
      {whisperModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-background rounded-xl shadow-2xl w-full max-w-md mx-4 border border-border">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <MicrophoneIcon className="h-5 w-5 text-primary" />
                Whisper to Agent
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Only the agent will hear this message — the customer won't.
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              <textarea
                autoFocus
                rows={3}
                value={whisperText}
                onChange={(e) => setWhisperText(e.target.value)}
                placeholder="Type your message to the agent..."
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleWhisper();
                  }
                }}
              />
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end gap-3">
              <Button
                variant="outlined"
                onClick={() => {
                  setWhisperModal(null);
                  setWhisperText('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleWhisper}
                disabled={!whisperText.trim() || sendingWhisper}
              >
                {sendingWhisper ? 'Sending...' : 'Send Whisper'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveCallsSection;
